import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AutotextService } from '../autotext.service';
import { ExamsService } from '../exams.service';
import { LoginService } from '../login.service';
import { ExamDiagnosesService } from './exam-diagnoses.service';
import {
  DIAGNOSIS_NOTES_AUTOTEXT_ID,
  DiagnosisEntry,
  DiagnosisNode,
  ExamDiagnosis,
} from './exam-diagnoses.model';

/**
 * The diagnoses block that sits above Sign Exam on every exam.
 *
 * Two ways in: type to filter the catalogue, or drill down the category tree for
 * when you cannot recall the name. Either way the pick lands as its own line in
 * the list below, and free notes with Auto Text sit under that.
 *
 * The catalogue itself is loaded once at login by ExamDiagnosesService, so both
 * search and drill-down are in-memory here - no request per keystroke.
 */
@Component({
  selector: 'app-exam-diagnoses',
  templateUrl: './exam-diagnoses.component.html',
  styleUrls: ['./exam-diagnoses.component.scss'],
  standalone: false
})
export class ExamDiagnosesComponent implements OnInit, OnDestroy {
  search = new UntypedFormControl('');
  notes = new UntypedFormControl('');

  /** main and secondary together, in pick order; Kind tells them apart */
  diagnoses: ExamDiagnosis[] = [];
  /** while on, everything picked goes to the secondary diagnosis */
  secondaryMode = false;
  matches: DiagnosisEntry[] = [];
  /** true only in the rare window where a keystroke beat the catalogue in */
  searching = false;
  catalogFailed = false;

  showTree = false;
  /** the drill-down path: level[i] is the list shown at depth i */
  levels: DiagnosisNode[][] = [];
  crumbs: string[] = [];

  loaded = false;
  private subs = new Subscription();

  readonly notesAutoTextId = DIAGNOSIS_NOTES_AUTOTEXT_ID;

  constructor(
    public es: ExamsService,
    public ls: LoginService,
    public atS: AutotextService,
    private svc: ExamDiagnosesService
  ) {}

  ngOnInit() {
    this.load();

    this.subs.add(
      this.search.valueChanges.pipe(debounceTime(150)).subscribe((term) => this.runSearch(term))
    );

    this.subs.add(
      this.notes.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => this.persistNotes())
    );

    // the doctor pulled an earlier clinic exam: diagnoses and notes were replaced
    this.subs.add(this.es.clinicPrefilled$.subscribe(() => this.load()));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private load() {
    if (!this.es.examID) return;
    try {
      this.svc.get(this.es.examID).subscribe(
        (data: any) => {
          this.diagnoses = ((data && data.diagnoses) || []).map((d: ExamDiagnosis) => ({ ...d, Kind: d.Kind === 1 ? 1 : 0 }));
          this.notes.setValue((data && data.notes) || '', { emitEvent: false });
          if (this.es.isClosed) {
            this.search.disable({ emitEvent: false });
            this.notes.disable({ emitEvent: false });
          }
          this.loaded = true;
        },
        (err) => { console.log(err); this.loaded = true; }
      );
    } catch (e) {
      console.log(e);
      this.loaded = true;
    }
  }

  // ---------- search ----------

  private runSearch(term: string) {
    const t = (term || '').trim();
    if (!t) {
      this.matches = [];
      return;
    }
    if (this.svc.isReady) {
      this.matches = this.svc.filter(t);
      return;
    }
    // login normally has it in hand long before the first exam is open; this only
    // covers a reload straight onto an exam page
    this.searching = true;
    try {
      this.svc.ready().subscribe(
        (ok) => {
          this.searching = false;
          this.catalogFailed = !ok;
          if (ok) this.matches = this.svc.filter(this.search.value || '');
        },
        (err) => { console.log(err); this.searching = false; this.catalogFailed = true; }
      );
    } catch (e) {
      console.log(e);
      this.searching = false;
      this.catalogFailed = true;
    }
  }

  pickEntry(entry: DiagnosisEntry) {
    if (!entry) return;
    this.add({ Code: entry.c || null, Name: entry.n, Path: entry.p || null });
    this.resetSearch();
  }

  /** Whatever was typed, even when it is not in the catalogue. */
  pickTyped() {
    const name = (this.search.value || '').trim();
    if (!name) return;
    this.add({ Code: null, Name: name, Path: null });
    this.resetSearch();
  }

  private resetSearch() {
    this.search.setValue('', { emitEvent: false });
    this.matches = [];
  }

  // ---------- category tree ----------

  toggleTree() {
    this.showTree = !this.showTree;
    if (!this.showTree || this.levels.length) return;
    if (this.svc.isReady) {
      this.levels = [this.svc.getRoots()];
      return;
    }
    this.searching = true;
    try {
      this.svc.ready().subscribe(
        (ok) => {
          this.searching = false;
          this.catalogFailed = !ok;
          if (ok) this.levels = [this.svc.getRoots()];
        },
        (err) => { console.log(err); this.searching = false; this.catalogFailed = true; }
      );
    } catch (e) {
      console.log(e);
      this.searching = false;
      this.catalogFailed = true;
    }
  }

  /** A category opens the next level; a diagnosis is added to the list. */
  openNode(node: DiagnosisNode, depth: number) {
    if (!node) return;
    if (node.hasChildren) {
      const next = this.svc.childrenOf(node);
      if (!next.length) return;
      this.levels = this.levels.slice(0, depth + 1).concat([next]);
      this.crumbs = this.crumbs.slice(0, depth).concat([node.label]);
      return;
    }
    if (node.entry) this.pickEntry(node.entry);
  }

  /** Back to a breadcrumb: -1 goes back to the department list. */
  goToCrumb(i: number) {
    this.levels = this.levels.slice(0, i + 2);
    this.crumbs = this.crumbs.slice(0, i + 1);
  }

  get currentLevel(): DiagnosisNode[] {
    return this.levels.length ? this.levels[this.levels.length - 1] : [];
  }

  // ---------- the selected list ----------

  get mainDiagnoses(): ExamDiagnosis[] {
    return this.diagnoses.filter((d) => d.Kind !== 1);
  }

  get secondaryDiagnoses(): ExamDiagnosis[] {
    return this.diagnoses.filter((d) => d.Kind === 1);
  }

  toggleSecondary() {
    this.secondaryMode = !this.secondaryMode;
  }

  private add(d: ExamDiagnosis) {
    if (this.es.isClosed) return;
    const key = (d.Name || '').trim().toLowerCase();
    if (!key) return;
    // the same diagnosis cannot be both main and secondary
    if (this.diagnoses.some((x) => (x.Name || '').trim().toLowerCase() === key)) return;
    this.diagnoses = this.diagnoses.concat([{ ...d, Kind: this.secondaryMode ? 1 : 0 }]);
    this.persist();
  }

  /** Double-click removes, the convention used across the exam pages. */
  remove(d: ExamDiagnosis) {
    if (this.es.isClosed) return;
    this.diagnoses = this.diagnoses.filter((x) => x !== d);
    this.persist();
  }

  private persist() {
    try {
      this.svc.save(this.es.examID, this.diagnoses).subscribe(
        () => {},
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }

  private persistNotes() {
    try {
      this.svc.saveNotes(this.es.examID, this.notes.value || '').subscribe(
        () => {},
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }
}
